import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Loader2, Play, Plus, X } from 'lucide-react';
import { diagnosticsAPI } from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';
import { toast } from 'sonner';

/**
 * Balance Sheet Census diagnostic card.
 *
 * Phase A: runs GET /admin/balance-sheet-census, displays per-entity coverage
 * of owned properties, valuations, loans, and receivables.
 *
 * Phase B: inline entry forms for property valuations and loan balances.
 */
export function BalanceSheetCensusPanel() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [expandedEntity, setExpandedEntity] = useState(null);
  const [valuationForm, setValuationForm] = useState(null);
  const [loanForm, setLoanForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const runCensus = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: d } = await diagnosticsAPI.balanceSheetCensus();
      setData(d);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveValuation = async () => {
    if (!valuationForm) return;
    setSaving(true);
    try {
      await diagnosticsAPI.createPropertyValuation(valuationForm);
      toast.success('Valuation saved');
      setValuationForm(null);
      // Re-run census to refresh coverage
      await runCensus();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveLoan = async () => {
    if (!loanForm) return;
    setSaving(true);
    try {
      await diagnosticsAPI.createPropertyLoan(loanForm);
      toast.success('Loan saved');
      setLoanForm(null);
      await runCensus();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const entitiesWithProperties = (data?.entities ?? []).filter(
    (e) => e.owned_property_count > 0
  );
  const entitiesWithReceivables = (data?.entities ?? []).filter(
    (e) =>
      (e.receivables_pennylane?.count ?? 0) > 0 ||
      (e.receivables_bookings?.count ?? 0) > 0 ||
      (e.receivables_bros?.count ?? 0) > 0
  );

  return (
    <Card data-testid="balance-sheet-census-panel">
      <CardHeader>
        <CardTitle className="text-base">Balance Sheet Census</CardTitle>
        <CardDescription>
          Per-entity coverage of owned properties, valuations, loans, and receivables
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Button
          variant="outline"
          size="sm"
          onClick={runCensus}
          disabled={loading}
          data-testid="run-census-btn"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin mr-1" />
          ) : (
            <Play className="h-4 w-4 mr-1" />
          )}
          Run Census
        </Button>

        {error && (
          <div className="text-sm text-red-600">{error}</div>
        )}

        {data && (
          <div className="space-y-4">
            {/* Property coverage table */}
            <div>
              <h4 className="text-sm font-medium mb-2">Owned Properties and Coverage</h4>
              {entitiesWithProperties.length === 0 ? (
                <p className="text-xs text-muted-foreground">No entities have owned-tenure properties.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="census-property-table">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="py-1 pr-3 font-medium">Entity</th>
                        <th className="py-1 pr-3 font-medium">Country</th>
                        <th className="py-1 pr-3 font-medium text-right">Owned</th>
                        <th className="py-1 pr-3 font-medium text-right">Valuations</th>
                        <th className="py-1 pr-3 font-medium text-right">Loans</th>
                        <th className="py-1 font-medium" />
                      </tr>
                    </thead>
                    <tbody>
                      {entitiesWithProperties.map((e) => (
                        <tr key={e.entity_id} className="border-b last:border-0">
                          <td className="py-1 pr-3">{e.entity_name}</td>
                          <td className="py-1 pr-3">{e.country}</td>
                          <td className="py-1 pr-3 text-right font-mono">{e.owned_property_count}</td>
                          <td className="py-1 pr-3 text-right font-mono">
                            <Badge
                              variant={e.valuation_coverage === 'n/a' ? 'outline' : 'secondary'}
                              className="text-[10px]"
                            >
                              {e.valuation_coverage}
                            </Badge>
                          </td>
                          <td className="py-1 pr-3 text-right font-mono">
                            <Badge
                              variant={e.loan_coverage === 'n/a' ? 'outline' : 'secondary'}
                              className="text-[10px]"
                            >
                              {e.loan_coverage}
                            </Badge>
                          </td>
                          <td className="py-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-5 text-[10px] px-1"
                              onClick={() =>
                                setExpandedEntity(expandedEntity === e.entity_id ? null : e.entity_id)
                              }
                            >
                              {expandedEntity === e.entity_id ? 'Hide' : 'Edit'}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Inline valuation/loan entry for expanded entity */}
            {expandedEntity && (
              <div className="border rounded-md p-3 space-y-3" data-testid="census-entry-form">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium">
                    Enter data for {entitiesWithProperties.find((e) => e.entity_id === expandedEntity)?.entity_name}
                  </h4>
                  <Button variant="ghost" size="sm" onClick={() => setExpandedEntity(null)}>
                    <X className="h-3 w-3" />
                  </Button>
                </div>

                {/* Valuation form */}
                <div className="space-y-2">
                  <p className="text-xs font-medium">Add Property Valuation</p>
                  {valuationForm ? (
                    <div className="flex items-end gap-2 flex-wrap">
                      <div>
                        <label className="text-[10px] text-muted-foreground">Property ID</label>
                        <Input
                          value={valuationForm.property_id}
                          onChange={(e) => setValuationForm({ ...valuationForm, property_id: e.target.value })}
                          className="h-6 text-xs w-[120px]"
                          placeholder="e.g. 12345"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-muted-foreground">Platform</label>
                        <Input
                          value={valuationForm.property_platform}
                          onChange={(e) => setValuationForm({ ...valuationForm, property_platform: e.target.value })}
                          className="h-6 text-xs w-[90px]"
                          placeholder="uplisting"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-muted-foreground">Valuation</label>
                        <Input
                          value={valuationForm.valuation}
                          onChange={(e) => setValuationForm({ ...valuationForm, valuation: e.target.value })}
                          className="h-6 text-xs w-[120px]"
                          placeholder="500000.00"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-muted-foreground">Currency</label>
                        <Input
                          value={valuationForm.currency}
                          onChange={(e) => setValuationForm({ ...valuationForm, currency: e.target.value })}
                          className="h-6 text-xs w-[60px]"
                          placeholder="EUR"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-muted-foreground">As-of Date</label>
                        <Input
                          type="date"
                          value={valuationForm.as_of_date}
                          onChange={(e) => setValuationForm({ ...valuationForm, as_of_date: e.target.value })}
                          className="h-6 text-xs w-[120px]"
                        />
                      </div>
                      <Button size="sm" className="h-6 text-xs" onClick={handleSaveValuation} disabled={saving}>
                        {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
                      </Button>
                      <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setValuationForm(null)}>
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-6 text-xs"
                      onClick={() =>
                        setValuationForm({
                          property_id: '',
                          property_platform: 'uplisting',
                          valuation: '',
                          currency: 'EUR',
                          as_of_date: new Date().toISOString().slice(0, 10),
                          source: 'manual',
                        })
                      }
                    >
                      <Plus className="h-3 w-3 mr-1" /> Add Valuation
                    </Button>
                  )}
                </div>

                {/* Loan form */}
                <div className="space-y-2">
                  <p className="text-xs font-medium">Add Loan Balance</p>
                  {loanForm ? (
                    <div className="flex items-end gap-2 flex-wrap">
                      <div>
                        <label className="text-[10px] text-muted-foreground">Property ID</label>
                        <Input
                          value={loanForm.property_id}
                          onChange={(e) => setLoanForm({ ...loanForm, property_id: e.target.value })}
                          className="h-6 text-xs w-[120px]"
                          placeholder="e.g. 12345"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-muted-foreground">Platform</label>
                        <Input
                          value={loanForm.property_platform}
                          onChange={(e) => setLoanForm({ ...loanForm, property_platform: e.target.value })}
                          className="h-6 text-xs w-[90px]"
                          placeholder="uplisting"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-muted-foreground">Lender</label>
                        <Input
                          value={loanForm.lender}
                          onChange={(e) => setLoanForm({ ...loanForm, lender: e.target.value })}
                          className="h-6 text-xs w-[120px]"
                          placeholder="Bank name"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-muted-foreground">Outstanding</label>
                        <Input
                          value={loanForm.outstanding_principal}
                          onChange={(e) => setLoanForm({ ...loanForm, outstanding_principal: e.target.value })}
                          className="h-6 text-xs w-[120px]"
                          placeholder="350000.00"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-muted-foreground">Currency</label>
                        <Input
                          value={loanForm.currency}
                          onChange={(e) => setLoanForm({ ...loanForm, currency: e.target.value })}
                          className="h-6 text-xs w-[60px]"
                          placeholder="EUR"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-muted-foreground">As-of Date</label>
                        <Input
                          type="date"
                          value={loanForm.as_of_date}
                          onChange={(e) => setLoanForm({ ...loanForm, as_of_date: e.target.value })}
                          className="h-6 text-xs w-[120px]"
                        />
                      </div>
                      <Button size="sm" className="h-6 text-xs" onClick={handleSaveLoan} disabled={saving}>
                        {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
                      </Button>
                      <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setLoanForm(null)}>
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-6 text-xs"
                      onClick={() =>
                        setLoanForm({
                          property_id: '',
                          property_platform: 'uplisting',
                          lender: '',
                          outstanding_principal: '',
                          currency: 'EUR',
                          as_of_date: new Date().toISOString().slice(0, 10),
                        })
                      }
                    >
                      <Plus className="h-3 w-3 mr-1" /> Add Loan
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Receivables table */}
            <div>
              <h4 className="text-sm font-medium mb-2">Receivable Candidates</h4>
              {entitiesWithReceivables.length === 0 ? (
                <p className="text-xs text-muted-foreground">No receivable candidates found.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="census-receivables-table">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="py-1 pr-3 font-medium">Entity</th>
                        <th className="py-1 pr-3 font-medium text-right">Pennylane (count)</th>
                        <th className="py-1 pr-3 font-medium text-right">Pennylane (sum)</th>
                        <th className="py-1 pr-3 font-medium text-right">Bookings (count)</th>
                        <th className="py-1 pr-3 font-medium text-right">Bookings (sum)</th>
                        <th className="py-1 pr-3 font-medium text-right">BR OS (count)</th>
                        <th className="py-1 pr-3 font-medium text-right">BR OS (sum)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entitiesWithReceivables.map((e) => (
                        <tr key={e.entity_id} className="border-b last:border-0">
                          <td className="py-1 pr-3">{e.entity_name}</td>
                          <td className="py-1 pr-3 text-right font-mono">{e.receivables_pennylane?.count ?? 0}</td>
                          <td className="py-1 pr-3 text-right font-mono">{e.receivables_pennylane?.sum ?? 0}</td>
                          <td className="py-1 pr-3 text-right font-mono">{e.receivables_bookings?.count ?? 0}</td>
                          <td className="py-1 pr-3 text-right font-mono">{e.receivables_bookings?.sum ?? 0}</td>
                          <td className="py-1 pr-3 text-right font-mono">{e.receivables_bros?.count ?? '-'}</td>
                          <td className="py-1 pr-3 text-right font-mono">{e.receivables_bros?.sum ?? '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Unassigned properties */}
            {data.unassigned_count > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2 text-amber-700">
                  Unassigned Properties ({data.unassigned_count})
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="census-unassigned-table">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="py-1 pr-3 font-medium">Collection</th>
                        <th className="py-1 pr-3 font-medium">Property ID</th>
                        <th className="py-1 pr-3 font-medium">Name</th>
                        <th className="py-1 pr-3 font-medium">entity_id</th>
                        <th className="py-1 pr-3 font-medium">entity_uuid</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.unassigned_properties.map((p, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="py-1 pr-3">{p.collection}</td>
                          <td className="py-1 pr-3 font-mono">{p.property_id}</td>
                          <td className="py-1 pr-3">{p.name}</td>
                          <td className="py-1 pr-3 font-mono text-amber-600">{p.entity_id ?? 'null'}</td>
                          <td className="py-1 pr-3 font-mono text-amber-600">{p.entity_uuid ?? 'null'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="text-[10px] text-muted-foreground">
              Generated at {data.generated_at}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
